import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import CollaboratorSection from './CollaboratorSection';
import AssignmentSection from './AssignmentSection';
import HeaderSection from './HeaderSection';
import TopSection from './TopSection';
import useApi from '../../hooks/useApi';
import useLocalStorage from '../../hooks/useLocalStorage';

import { PanelWrapper } from './styles';

const LoopContentViewer = ({ options, panel, fileService, flash }) => {
  const { userToken, circle_uuid, session_uuid, circle_state: state } = options;

  const { getData, loading } = useApi(userToken);

  const [data, setData] = useState();
  const [translations, setTranslations] = useState();
  const [loopSettings, setLoopSettings] = useState({});
  const [participations, setParticipations] = useState([]);
  const [hubs, setHubs] = useState([]);
  const [progress, setProgress] = useState();

  const collapsedDescriptionKey = `collapsedDescription_${window.viewer.userUuid}_${circle_uuid}`;
  const [collapsedDescription, setCollapsedDescription] = useLocalStorage(collapsedDescriptionKey, false);

  const isSession = state === 'session';

  useEffect(() => {
    document.addEventListener('notification.created', handleNotification);
    return () => document.removeEventListener('notification.created', handleNotification);
  }, [hubs, progress]);

  const initializeProgress = () => {
    const buckets = {};
    ['yellow', 'green', 'blue', 'pink', 'purple'].forEach((bucket) => {
      buckets[bucket] = { completed: 0, total: 0, text: '0/0', number: 0 };
    });
    return buckets;
  };

  const updateBucket = (bucket, node) => {
    bucket.completed = bucket.completed + (node.completed ? 1 : 0);
    bucket.total = bucket.total + 1;
    bucket.text = `${bucket.completed}/${bucket.total}`;
    bucket.percent = Math.round((bucket.completed / bucket.total) * 100);
  };

  const handleNotification = (event) => {
    const { report, node, template_key } = event.detail || {};
    const state = report?.state;
    const uuid = node?.uuid;
    const buckets = initializeProgress();
    const isAnnouncement = template_key === 'announce';
    const updatedHubs = hubs.map((hub) => ({
      ...hub,
      nodes: hub.nodes.map((node) => {
        let newNode = { ...node };
        if (node.uuid === uuid) {
          const accepted = report ? state === 'accepted' : node.accepted;
          const completed = report ? accepted || state === 'done' : node.completed;
          const hasAnyUnreadNotifications = report ? node.hasAnyUnreadNotifications : !isAnnouncement;
          const hasAnnounceNotification = report ? node.hasAnnounceNotification : isAnnouncement;
          newNode = { ...node, accepted, completed, hasAnyUnreadNotifications, hasAnnounceNotification };
        }
        updateBucket(buckets[node.bucket], newNode);
        return newNode;
      }),
    }));
    setProgress(buckets);
    setHubs(updatedHubs);
  };

  useEffect(() => {
    const getLoopData = async () => {
      if (!loading) {
        const response = isSession
          ? await getData(`/api/v3/sessions/${session_uuid}/loop`)
          : await getData(`/api/v3/viewer/circles/${circle_uuid}`);
        if (response.success) {
          const { loop, settings, translations, orderedHubs, progress } = response.body;
          setData(loop);
          setLoopSettings(settings);
          setTranslations(translations);
          setParticipations(settings.participations || []);
          setHubs(orderedHubs);
          setProgress(isSession ? progress[settings?.user?.id] : {});
        }
      }
    };
    getLoopData();
  }, []);

  if (loading || !translations || !progress) {
    return null;
  }

  const getSortKey = (participant) => `${participant.User.full_name}`;
  const collaborators = participations.sort((a, b) => getSortKey(a).localeCompare(getSortKey(b)));

  const showDescription = () => setCollapsedDescription(false);
  const hideDescription = () => setCollapsedDescription(true);

  return (
    <PanelWrapper className={'panelContent ir-content'}>
      <TopSection
        panel={panel}
        options={options}
        nodeData={data}
        translations={translations}
        collapsedDescription={collapsedDescription}
        showDescription={showDescription}
      />
      {!collapsedDescription && (
        <HeaderSection
          data={data}
          options={options}
          translations={translations}
          loopSettings={loopSettings}
          flash={flash}
          hideDescription={hideDescription}
        />
      )}
      <AssignmentSection
        fileService={fileService}
        hubs={hubs}
        translations={translations}
        isSession={isSession}
        progress={progress}
      />
      <CollaboratorSection
        options={options}
        fileService={fileService}
        collaborators={collaborators}
        isSession={isSession}
        translations={translations}
      />
    </PanelWrapper>
  );
};

export default LoopContentViewer;

LoopContentViewer.propTypes = {
  options: PropTypes.object.isRequired,
  panel: PropTypes.object.isRequired,
  fileService: PropTypes.object.isRequired,
  flash: PropTypes.func.isRequired,
};
